'use client'
import type { TextFieldProps } from '@mui/material/TextField'
import { useRef } from 'react'

import { transformValue, transformValueOnBlur, transformValueOnChange } from 'minimal-shared/utils'

import { useBoolean } from '@/hooks'
import { Box, InputAdornment, useMediaQuery, useTheme } from '@mui/material'
import TextField from '@mui/material/TextField'

// Components
import { ErrorTooltipIcon, type ErrorTooltipIconRef, FieldLabel, PasswordToggleIcon } from './components'
// Styles
import { getErrorBorderStyles } from './styles'

// ----------------------------------------------------------------------

export type TextFieldCTProps = TextFieldProps & {
  classContainer?: string
  width?: string | number
  showLabel?: boolean
  errorMessage?: string
  onValueChange?: (value: string) => void
}

export function TextFieldCT({
  helperText,
  slotProps,
  classContainer,
  type = 'text',
  label,
  width = '100%',
  required,
  showLabel = true,
  errorMessage,
  value,
  onChange,
  onValueChange,
  ...other
}: TextFieldCTProps) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isNumberType = type === 'number'
  const showPassword = useBoolean()
  const errorIconRef = useRef<ErrorTooltipIconRef>(null)

  const hasError = !!errorMessage

  const handleInputClick = () => {
    // Ẩn tooltip khi click vào input
    if (errorIconRef.current) {
      errorIconRef.current.hideTooltip()
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (other.disabled) return

    const transformedValue = isNumberType ? transformValueOnChange(event.target.value) : event.target.value

    // Call original onChange if provided
    if (onChange) {
      const newEvent = {
        ...event,
        target: { ...event.target, value: transformedValue },
      }
      onChange(newEvent as React.ChangeEvent<HTMLInputElement>)
    }

    // Call onValueChange callback if provided
    if (onValueChange) {
      onValueChange(transformedValue)
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const transformedValue = isNumberType ? transformValueOnBlur(event.target.value) : event.target.value

    // Call original onChange to update value on blur (for number formatting)
    if (onChange && isNumberType) {
      const newEvent = {
        ...event,
        target: { ...event.target, value: transformedValue },
      }
      onChange(newEvent as any)
    }

    // Call original onBlur if provided
    if (other.onBlur) {
      other.onBlur(event)
    }
  }

  return (
    <Box
      className={classContainer}
      sx={{
        position: 'relative',
        width: width,
      }}
    >
      {showLabel && label && <FieldLabel label={label} required={required} />}
      <TextField
        fullWidth
        value={isNumberType ? transformValue(value as any) : (value ?? '')}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={handleInputClick}
        type={isNumberType ? 'text' : type === 'password' && showPassword.value ? 'text' : type}
        error={hasError}
        // Show helperText only on mobile
        helperText={!isDesktop ? (errorMessage ?? helperText) : helperText}
        slotProps={{
          ...slotProps,
          htmlInput: {
            autoComplete: 'off',
            ...slotProps?.htmlInput,
            ...(isNumberType && { inputMode: 'decimal', pattern: '[0-9]*\\.?[0-9]*' }),
          },
          inputLabel: {
            ...(isNumberType && { shrink: true }),
            ...slotProps?.inputLabel,
            style: {
              fontSize: '0.875rem',
              top: '2px',
            },
          },
          formHelperText: {
            ...(hasError && !isDesktop && { error: true }),
            ...slotProps?.formHelperText,
          },
          input: {
            ...slotProps?.input,
            endAdornment: (
              <InputAdornment position="end">
                {(slotProps?.input as any)?.endAdornment}
                {/* Error Icon với MUI Tooltip */}
                {hasError && (
                  <ErrorTooltipIcon
                    ref={errorIconRef}
                    errorMessage={errorMessage || ''}
                    showTooltip={isDesktop}
                    hasPasswordIcon={type === 'password'}
                    isSubmitted={true}
                  />
                )}
                {/* Password Toggle Icon */}
                {type === 'password' && (
                  <PasswordToggleIcon
                    showPassword={showPassword.value}
                    onToggle={showPassword.onToggle}
                    disabled={other.disabled}
                  />
                )}
              </InputAdornment>
            ),
          },
        }}
        sx={
          {
            ...other.sx,
            ...getErrorBorderStyles(hasError),
          } as any
        }
        {...other}
      />
    </Box>
  )
}
